import { isEmailMessage } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/email";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { GetUser } from "@redotech/redo-model/user";
import { Flex } from "@redotech/redo-web/flex";
import { Fragment, memo, useMemo, useState } from "react";
import type { DraftCollectionItem } from "../../db/collections";
import { useThreadMessages } from "../../hooks/use-conversation-data";
import { useEmailThreadView } from "../../hooks/use-email-thread-view";
import { useConversationDrafts } from "../../providers/conversation-drafts-context";
import { MessageInput } from "../message-input";
import { EmailMessageCard } from "../message-types/email-message-card";
import {
  type FormattedInternalNote,
  InternalNoteCard,
  formatInternalNote,
  isInternalNoteMessage,
} from "../message-types/internal-note-card";
import { SendingDraftCard } from "../message-types/sending-draft-card";
import { ThreadHeader } from "../thread-header";

/**
 * Email thread view component.
 * Fetches its own messages via useThreadMessages and uses useEmailThreadView
 * for display state management.
 */
export const EmailThreadView = memo(function EmailThreadView({
  thread,
}: {
  thread: ConversationThread;
}) {
  const { data: messages } = useThreadMessages(
    thread.conversationId,
    thread.id,
  );

  // Get drafts from context (fetched once at conversation level)
  const { drafts: allConversationDrafts } = useConversationDrafts();

  // Filter to drafts for this specific thread
  const threadDrafts = useMemo(
    () =>
      allConversationDrafts.filter(
        (d: DraftCollectionItem) => d.threadId === String(thread.id),
      ),
    [allConversationDrafts, thread.id],
  );

  const allDrafts = threadDrafts;

  const team = useRequiredContext(TeamContext);
  const userNameMap = useMemo(
    () =>
      new Map(
        team.users.map((u) => {
          const user = u.user as GetUser;
          return [user._id, user.name];
        }),
      ),
    [team.users],
  );

  // Filter to email messages only (for email-specific formatting)
  const emailMessages = useMemo(() => {
    return messages.filter((m) => isEmailMessage(m));
  }, [messages]);

  // Format internal notes
  const formattedNotes = useMemo(() => {
    const noteMessages = messages.filter(isInternalNoteMessage);
    return noteMessages.map((m) => formatInternalNote(m, userNameMap));
  }, [messages, userNameMap]);

  const {
    formattedMessages,
    isMessageExpanded,
    toggleMessageExpanded,
    isReplyOpen,
    openReply,
    closeReply,
    getReplyMode,
  } = useEmailThreadView(emailMessages, threadDrafts);

  // Build a combined timeline of email messages and internal notes
  type TimelineItem =
    | { kind: "email"; message: (typeof formattedMessages)[number] }
    | { kind: "internalNote"; note: FormattedInternalNote };

  const timeline = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [
      ...formattedMessages.map((message) => ({
        kind: "email" as const,
        message,
      })),
      ...formattedNotes.map((note) => ({
        kind: "internalNote" as const,
        note,
      })),
    ];
    items.sort((a, b) => {
      const timeA = a.kind === "email" ? a.message.timestamp : a.note.timestamp;
      const timeB = b.kind === "email" ? b.message.timestamp : b.note.timestamp;
      return timeA.getTime() - timeB.getTime();
    });
    return items;
  }, [formattedMessages, formattedNotes]);

  // Extract subject from thread channel data if available
  const threadSubject =
    thread.integrationType === "gmail" || thread.integrationType === "outlook"
      ? (thread.channelData?.subject ?? null)
      : null;

  const [isCollapsed, setIsCollapsed] = useState(false);
  const toggleCollapsed = useHandler(() => setIsCollapsed((prev) => !prev));

  if (timeline.length === 0) {
    // Empty thread — typically a freshly created draft thread (e.g. clicking
    // "Reply with email" on a contact form submission). Render the composer
    // inline so the merchant can send the first message in this thread.
    const draft = threadDrafts[0];
    const replyRecipients = {
      to: draft?.channelData?.to ? [...draft.channelData.to] : [],
      cc: draft?.channelData?.cc ? [...draft.channelData.cc] : [],
      bcc: draft?.channelData?.bcc ? [...draft.channelData.bcc] : [],
    };
    return (
      <Flex dir="column" gap="none">
        <ThreadHeader
          channel={thread.channel}
          isCollapsed={isCollapsed}
          onClick={toggleCollapsed}
          subject={threadSubject}
        />
        {!isCollapsed && (
          <Flex
            bgColor="primary"
            borderBottomWidth="1px"
            borderColor="primary"
            borderLeftWidth="1px"
            borderRightWidth="1px"
            borderStyle="solid"
            bottomLeftRadius="sm"
            bottomRightRadius="sm"
            dir="column"
            p="xl"
          >
            <MessageInput
              channel={thread.channel}
              conversationId={thread.conversationId}
              mode="email"
              replyRecipients={replyRecipients}
              threadId={thread.id}
            />
          </Flex>
        )}
      </Flex>
    );
  }

  const lastItem = timeline[timeline.length - 1];
  const lastItemTimestamp = lastItem
    ? lastItem.kind === "email"
      ? lastItem.message.timestamp
      : lastItem.note.timestamp
    : undefined;

  return (
    <Flex dir="column" gap="none">
      <ThreadHeader
        channel={thread.channel}
        isCollapsed={isCollapsed}
        latestMessageTime={lastItemTimestamp || undefined}
        onClick={toggleCollapsed}
        subject={threadSubject}
      />
      {!isCollapsed &&
        timeline.map((item, index) => {
          const isLastItem = index === timeline.length - 1;

          if (item.kind === "internalNote") {
            return (
              <InternalNoteCard
                isLast={isLastItem}
                key={item.note.id}
                note={item.note}
              />
            );
          }

          const { message } = item;
          const draft = allDrafts.find(
            (d: DraftCollectionItem) =>
              d.inReplyToMessageId === message.messageId,
          );
          const isSending = draft?.status === "SENDING";
          return (
            <Fragment key={message.id}>
              <EmailMessageCard
                channel={thread.channel}
                conversationId={thread.conversationId}
                initialReplyMode={getReplyMode(message.messageId)}
                isExpanded={isMessageExpanded(message.id)}
                isLast={isLastItem && !isSending}
                isReplyOpen={isReplyOpen(message.messageId) && !isSending}
                message={message}
                onDiscardDraft={() => closeReply(message.messageId)}
                onForward={() => openReply(message.messageId, "forward")}
                onReply={() => openReply(message.messageId, "reply")}
                onReplyAll={() => openReply(message.messageId, "replyAll")}
                onToggleExpand={() => toggleMessageExpanded(message.id)}
              />
              {isSending && draft && (
                <SendingDraftCard draft={draft} isLast={isLastItem} />
              )}
            </Fragment>
          );
        })}
    </Flex>
  );
});
